import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal, Card, Tag, Typography, Space, Progress, Statistic } from 'antd';
import { 
  LockOutlined, 
  CrownOutlined, 
  RocketOutlined, 
  StarOutlined,
  InfoCircleOutlined 
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { planService, PLANS } from '../../services/planService';

const { Title, Text, Paragraph } = Typography;

/**
 * Composant pour contrôler l'accès aux fonctionnalités selon le plan
 */
const PlanGate = ({ 
  feature, 
  children, 
  fallback, 
  showUpgrade = true, 
  type = 'feature', // 'feature', 'limit', 'route'
  currentCount, 
  limitType,
  className 
}) => {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [messageLimit, setMessageLimit] = useState(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);

  useEffect(() => {
    // Éviter les appels multiples si une vérification est déjà en cours
    if (!isCheckingAccess) {
      checkAccess();
    }
  }, [feature, type, currentCount, limitType]);

  /**
   * Vérifier l'accès selon le type de contrôle
   */
  const checkAccess = async () => {
    // Éviter les appels multiples simultanés
    if (isCheckingAccess) {
      return;
    }

    setIsCheckingAccess(true);
    setLoading(true);
    
    try {
      // Récupérer les informations du plan
      const planData = await planService.getCurrentPlan();
      setPlanInfo(planData);
      
      let access = false;

      switch (type) {
        case 'feature':
          access = await planService.hasFeature(feature);
          break;
        
        case 'limit':
          if (limitType && typeof currentCount === 'number') {
            const limitCheck = await planService.canCreate(limitType, currentCount);
            setLimitInfo(limitCheck);
            access = limitCheck.allowed;
          }
          break;
        
        case 'route':
          access = await planService.canAccessRoute(feature);
          break;
        
        case 'message':
          const msgLimit = await planService.checkMessageLimit();
          setMessageLimit(msgLimit);
          access = msgLimit.allowed;
          break;
        
        default:
          access = true;
      }
      
      setHasAccess(access);
    } catch (error) {
      setHasAccess(false);
    } finally {
      setLoading(false);
      setIsCheckingAccess(false);
    }
  };

  /**
   * Obtenir l'icône du plan
   */
  const getPlanIcon = (plan) => {
    const icons = {
      [PLANS.DECOUVERTE]: <InfoCircleOutlined />,
      [PLANS.ESSENTIEL]: <RocketOutlined />,
      [PLANS.CROISSANCE]: <StarOutlined />,
      [PLANS.EXCELLENCE]: <CrownOutlined />
    };
    return icons[plan] || <LockOutlined />;
  };

  /**
   * Obtenir la couleur du plan
   */
  const getPlanColor = (plan) => {
    const colors = {
      [PLANS.DECOUVERTE]: 'default',
      [PLANS.ESSENTIEL]: 'blue',
      [PLANS.CROISSANCE]: 'cyan',
      [PLANS.EXCELLENCE]: 'purple'
    };
    return colors[plan] || 'default';
  };

  /**
   * Rendu du modal de mise à niveau
   */
  const renderUpgradeModal = () => {
    console.log('renderUpgradeModal',type);
    console.log('feature',feature);
    const recommendation = feature ? planService.getUpgradeRecommendation(feature) : null;
    console.log('recommendation',recommendation);
    return (
      <Modal
        title={
          <Space>
            <CrownOutlined style={{ color: '#f39c12' }} />
            <span>Mise à niveau requise</span>
          </Space>
        }
        open={upgradeModalVisible}
        onCancel={() => setUpgradeModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setUpgradeModalVisible(false)}>
            Plus tard
          </Button>,
          <Link key="upgrade" to="/pricing">
            <Button type="primary" onClick={() => setUpgradeModalVisible(false)}>
              Voir les plans
            </Button>
          </Link>
        ]}
        width={600}
      >
        <div className="space-y-4">
          {/* Informations du plan actuel */}
          <Card size="small">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Tag color={getPlanColor(planInfo?.billing_plan)} icon={getPlanIcon(planInfo?.billing_plan)}>
                  Plan {planService.getPlanDisplayName(planInfo?.billing_plan)}
                </Tag>
                {planInfo?.is_trial && (
                  <Tag color="orange">Version d'essai</Tag>
                )}
              </div>
            </div>
          </Card>

          {/* Limitation actuelle */}
          {type === 'limit' && limitInfo && (
            <Alert
              message="Limite atteinte"
              description={`Votre plan actuel vous permet d'avoir ${limitInfo.limit} ${limitType}(s). Vous en avez actuellement ${limitInfo.current}.`}
              type="warning"
              showIcon
            />
          )}

          {/* Limite de messages */}
          {type === 'message' && messageLimit && (
            <Card>
              <Statistic
                title="Messages utilisés ce mois"
                value={messageLimit.used}
                suffix={messageLimit.total === -1 ? "/ Illimité" : `/ ${messageLimit.total}`}
              />
              {messageLimit.total !== -1 && (
                <Progress 
                  percent={Math.round((messageLimit.used / messageLimit.total) * 100)} 
                  status={messageLimit.remaining === 0 ? "exception" : "normal"}
                  className="mt-2"
                />
              )}
            </Card>
          )}

          {/* Recommandation de plan */}
          {recommendation && (
            <Card className="border-blue-200 bg-blue-50">
              <div className="space-y-2">
                <Title level={5} className="mb-2">
                  <RocketOutlined className="mr-2" />
                  Plan recommandé: {recommendation.planName}
                </Title>
                <Paragraph className="text-sm text-gray-600 mb-0">
                  Pour accéder à cette fonctionnalité, nous recommandons de passer au plan {recommendation.planName}.
                </Paragraph>
              </div>
            </Card>
          )}

          {/* Fonctionnalité non disponible */}
          {type === 'feature' && (
            <Alert
              message="Fonctionnalité non disponible"
              description={`Cette fonctionnalité n'est pas incluse dans votre plan actuel. Consultez nos plans pour voir les options disponibles.`}
              type="info"
              showIcon
            />
          )}
        </div>
      </Modal>
    );
  };

  /**
   * Rendu du fallback par défaut
   */
  const renderDefaultFallback = () => {
    return (
      <div className={`p-6 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg text-center ${className || ''}`}>
        <div className="space-y-4">
          {/* Icône de verrouillage */}
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <LockOutlined className="text-2xl text-yellow-600" />
            </div>
          </div>

          {/* Message principal */}
          <div>
            <Title level={4} className="text-gray-700 mb-2">
              Fonctionnalité non disponible
            </Title>
            
            {type === 'limit' && limitInfo && (
              <Text className="text-gray-500">
                Limite atteinte: {limitInfo.current}/{limitInfo.limit} {limitType}(s)
              </Text>
            )}
            
            {type === 'message' && messageLimit && (
              <Text className="text-gray-500">
                Messages épuisés: {messageLimit.used}/{messageLimit.total === -1 ? "∞" : messageLimit.total}
              </Text>
            )}
            
            {type === 'feature' && (
              <Text className="text-gray-500">
                Cette fonctionnalité nécessite un plan supérieur
              </Text>
            )}
            
            {type === 'route' && (
              <Text className="text-gray-500">
                Cette page nécessite un abonnement supérieur
              </Text>
            )}
          </div>

          {/* Badge du plan actuel */}
          {planInfo && (
            <div className="flex justify-center">
              <Tag color={getPlanColor(planInfo.billing_plan)} icon={getPlanIcon(planInfo.billing_plan)}>
                Plan {planService.getPlanDisplayName(planInfo.billing_plan)}
              </Tag>
            </div>
          )}

          {/* Bouton de mise à niveau */}
          {showUpgrade && (
            <div className="space-y-2">
              <Button
                type="primary"
                icon={<CrownOutlined />}
                onClick={() => setUpgradeModalVisible(true)}
              >
                Voir les options
              </Button>
              <div>
                <Link to="/pricing" className="text-sm text-blue-600 hover:text-blue-800">
                  Comparer tous les plans
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
      </div>
    );
  }

  // Si l'accès est autorisé, afficher le contenu
  if (hasAccess) {
    return children;
  }

  // Sinon, afficher le fallback ou le fallback par défaut
  return (
    <>
      {fallback || renderDefaultFallback()}
      {renderUpgradeModal()}
    </>
  );
};

export default PlanGate; 